import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Checkbox } from '@/ui/base/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import type { MedicaoSelecionavel } from './tipos';

/**
 * Aprovação em lote do Ateste de Medição: abas por tipo de pessoa (PF/PJ/
 * Cooperativa), com totalizador por aba e checkbox por linha para excluir
 * itens individualmente da aprovação.
 *
 * Dado simulado por decisão de produto (ver `_dev/cenarioDeTela.ts`): não há
 * API ainda, então "aprovar" só atualiza o estado local da página.
 */
interface AprovarLoteAtesteModalProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Só as medições com status PROCESSADA — candidatas a validação. */
  candidatas: MedicaoSelecionavel[];
  onConfirmar: (medicaoIds: string[]) => void;
}

type TipoAba = 'PF' | 'PJ' | 'COOP';

const ABA_LABEL: Record<TipoAba, string> = {
  PF: 'Pessoa Física',
  PJ: 'Pessoa Jurídica',
  COOP: 'Cooperativa',
};

function abaDoTipo(tipo: string): TipoAba {
  if (tipo === 'PF') return 'PF';
  if (tipo === 'COOPERATIVA') return 'COOP';
  return 'PJ';
}

export function AprovarLoteAtesteModal({
  open,
  onOpenChange,
  candidatas,
  onConfirmar,
}: AprovarLoteAtesteModalProps) {
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (open) {
      setExcluidos(new Set());
      setEnviando(false);
    }
  }, [open]);

  const grupos = useMemo(() => {
    const g: Record<TipoAba, MedicaoSelecionavel[]> = { PF: [], PJ: [], COOP: [] };
    candidatas.forEach((c) => g[abaDoTipo(c.tipo)].push(c));
    return g;
  }, [candidatas]);

  const totalPorAba = (linhas: MedicaoSelecionavel[]) =>
    linhas.reduce(
      (acc, linha) => {
        if (!excluidos.has(linha.medicaoId)) {
          acc.valor += linha.valorTotal;
          acc.qtd += 1;
        }
        return acc;
      },
      { valor: 0, qtd: 0 },
    );

  const totalGeral = useMemo(
    () =>
      candidatas
        .filter((c) => !excluidos.has(c.medicaoId))
        .reduce(
          (acc, c) => {
            acc.valor += c.valorTotal;
            acc.qtd += 1;
            return acc;
          },
          { valor: 0, qtd: 0 },
        ),
    [candidatas, excluidos],
  );

  const desviosCount = candidatas.filter(
    (c) => c.desvio?.hasDesvio && !excluidos.has(c.medicaoId),
  ).length;

  const alternar = (medicaoId: string) => {
    setExcluidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(medicaoId)) proximo.delete(medicaoId);
      else proximo.add(medicaoId);
      return proximo;
    });
  };

  const handleAprovar = () => {
    const ids = candidatas.filter((c) => !excluidos.has(c.medicaoId)).map((c) => c.medicaoId);
    if (ids.length === 0) return;
    setEnviando(true);
    window.setTimeout(() => {
      onConfirmar(ids);
      setEnviando(false);
    }, 700);
  };

  const primeiraAbaComDados =
    (Object.keys(grupos) as TipoAba[]).find((aba) => grupos[aba].length > 0) ?? 'PF';

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      desktopMaxWidth="max-w-4xl"
      title={
        <>
          <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
          Aprovar lote — Ateste de Medição
        </>
      }
      description="Revise por tipo de pessoa. Itens desmarcados não entram nesta aprovação e podem ser tratados separadamente."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={handleAprovar} disabled={enviando || totalGeral.qtd === 0}>
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="h-4 w-4" aria-hidden />
            )}
            Aprovar {totalGeral.qtd} medição(ões) · {formatarDinheiro(totalGeral.valor)}
          </Button>
        </>
      }
    >
      {desviosCount > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-warning bg-warning/10 p-2 text-xs">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          <span>
            {desviosCount} item(ns) com <strong>desvio acima do limite</strong> ainda estão no lote.
            Considere revisar antes de aprovar.
          </span>
        </div>
      )}

      <Tabs defaultValue={primeiraAbaComDados}>
        <TabsList className="grid w-full grid-cols-3">
          {(Object.keys(grupos) as TipoAba[]).map((aba) => {
            const total = totalPorAba(grupos[aba]);
            return (
              <TabsTrigger key={aba} value={aba} className="text-xs">
                {ABA_LABEL[aba]}
                <Badge variant="secondary" className="ml-2 text-[10px]">
                  {total.qtd}/{grupos[aba].length}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {(Object.keys(grupos) as TipoAba[]).map((aba) => {
          const linhas = grupos[aba];
          const total = totalPorAba(linhas);
          return (
            <TabsContent key={aba} value={aba} className="mt-3">
              {linhas.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Sem medições validáveis deste tipo neste mês.
                </p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center gap-3 rounded-md bg-muted/50 px-3 py-2 text-xs">
                    <span>
                      Selecionadas:{' '}
                      <strong>
                        {total.qtd}/{linhas.length}
                      </strong>
                    </span>
                    <span className="ml-auto">
                      Total: <strong className="font-mono">{formatarDinheiro(total.valor)}</strong>
                    </span>
                  </div>
                  <div className="max-h-[42vh] overflow-y-auto rounded-md border border-border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-muted/50">
                        <tr>
                          <th className="w-10 p-2 text-left" />
                          <th className="p-2 text-left">Credenciado</th>
                          <th className="w-24 p-2 text-right">Valor</th>
                          <th className="w-24 p-2 text-left">Desvio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((linha) => {
                          const incluido = !excluidos.has(linha.medicaoId);
                          return (
                            <tr key={linha.medicaoId} className="border-t border-border">
                              <td className="p-2">
                                <Checkbox
                                  checked={incluido}
                                  onCheckedChange={() => alternar(linha.medicaoId)}
                                  aria-label={`Incluir ${linha.nome} na aprovação em lote`}
                                />
                              </td>
                              <td className="p-2">{linha.nome}</td>
                              <td className="p-2 text-right font-mono">
                                {formatarDinheiro(linha.valorTotal)}
                              </td>
                              <td className="p-2">
                                {linha.desvio?.hasDesvio ? (
                                  <Badge
                                    variant="outline"
                                    className="border-warning text-[10px] text-warning"
                                  >
                                    <AlertTriangle className="mr-1 h-3 w-3" aria-hidden />
                                    {linha.desvio.motivos[0]}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </ResponsiveModal>
  );
}

export default AprovarLoteAtesteModal;
